// AdminBetaCodesCard — mint & track lifetime-Pro codes for beta testers.
// Codes redeem via the existing /api/redeem-code endpoint (already grants
// plan=pro with no expiry). Use these to hand out to teachers on Instagram,
// early testers on Reddit, etc. — collect a testimonial in exchange.
import { useCallback, useEffect, useState } from "react";
import { Key, Copy, Check, Loader2, Plus, Users, Sparkles } from "lucide-react";
import { generateBetaCodes, listBetaCodes } from "@/lib/api";
import { toast } from "sonner";

const CodeRow = ({ code, redeemed, label, redeemedEmail, copied, onCopy }) => {
  const link = typeof window !== "undefined"
    ? `${window.location.origin}/register?code=${code}`
    : `/register?code=${code}`;
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background px-3 py-2"
      data-testid={`beta-code-row-${code}`}
    >
      <div className="flex-1 min-w-0">
        <p className={`font-mono text-sm ${redeemed ? "line-through text-muted-foreground" : "font-bold"}`}>{code}</p>
        {!redeemed && (
          <p className="text-[10px] text-muted-foreground truncate select-all" title={link}>
            {link}
          </p>
        )}
        {label && !redeemed && <p className="text-[10px] text-muted-foreground truncate">{label}</p>}
        {redeemed && redeemedEmail && (
          <p className="text-[11px] text-emerald-700">✓ {redeemedEmail}</p>
        )}
      </div>
      {!redeemed && (
        <button
          data-testid={`beta-code-copy-${code}`}
          onClick={() => onCopy(code)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-secondary/60 shrink-0"
        >
          {copied === code ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy link</>}
        </button>
      )}
    </div>
  );
};

export const AdminBetaCodesCard = ({ role }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [count, setCount] = useState(5);
  const [label, setLabel] = useState("");
  const [copied, setCopied] = useState(null);
  const [generating, setGenerating] = useState(false);

  const refresh = useCallback(async () => {
    try { setData(await listBetaCodes()); } catch (e) { setErr(e); }
  }, []);

  useEffect(() => {
    if (role !== "admin") return;
    refresh();
  }, [role, refresh]);

  if (role !== "admin") return null;
  if (err) return null;
  if (data === null) {
    return (
      <div data-testid="admin-beta-codes-card" className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading beta codes…
        </div>
      </div>
    );
  }

  const copy = async (code) => {
    try {
      // Copy the full redeem URL — when pasted into SMS / email / Facebook DM
      // it becomes a real tappable link that auto-fills the code on signup.
      // Users get lifetime Pro in one click instead of copy-paste-signup-paste-code.
      const link = `${window.location.origin}/register?code=${encodeURIComponent(code)}`;
      await navigator.clipboard.writeText(link);
      setCopied(code);
      toast.success("Sign-up link copied — paste it in a DM, text, or email");
      setTimeout(() => setCopied(null), 2200);
    } catch { toast.error("Couldn't copy"); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await generateBetaCodes(Math.max(1, Math.min(50, parseInt(count) || 1)), label.trim());
      toast.success(`Minted ${res.codes.length} lifetime code${res.codes.length === 1 ? "" : "s"}`);
      setLabel("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mint codes.");
    } finally {
      setGenerating(false);
    }
  };

  const active = data.active || [];
  const redeemed = data.redeemed || [];

  return (
    <div data-testid="admin-beta-codes-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/15 text-accent shrink-0">
          <Key className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-serif text-xl font-bold">Beta lifetime codes</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Mint one-time codes that redeem for <span className="font-semibold text-foreground">lifetime Pro</span>. Hand them out to beta testers in exchange for an honest testimonial.
            Redeem at <span className="font-mono">/pricing → Have a code?</span> or on the dashboard.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border p-3 text-center">
          <Key className="h-4 w-4 mx-auto text-accent mb-1" />
          <p className="font-serif text-2xl font-bold" data-testid="beta-codes-active-count">{active.length}</p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">active</p>
        </div>
        <div className="rounded-xl border border-border p-3 text-center">
          <Users className="h-4 w-4 mx-auto text-accent mb-1" />
          <p className="font-serif text-2xl font-bold" data-testid="beta-codes-redeemed-count">{redeemed.length}</p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">redeemed</p>
        </div>
      </div>

      {/* Generate */}
      <div className="mt-5 rounded-xl border border-dashed border-accent/40 bg-accent/5 p-4">
        <p className="text-xs font-bold uppercase tracking-widest text-accent">Mint new codes</p>
        <div className="mt-3 grid grid-cols-[80px_1fr_auto] gap-2 items-stretch">
          <input
            data-testid="beta-codes-count"
            type="number" min="1" max="50" value={count}
            onChange={(e) => setCount(e.target.value)}
            className="rounded-lg border border-border bg-background px-2 py-2 text-sm text-center font-mono"
          />
          <input
            data-testid="beta-codes-label"
            type="text" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (e.g. Reddit r/APStudents)"
            maxLength={80}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            data-testid="beta-codes-generate"
            onClick={generate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 text-sm font-bold hover:opacity-90 disabled:opacity-50"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Mint
          </button>
        </div>
      </div>

      {active.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Available to hand out</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {active.map((c) => (
              <CodeRow key={c.code} code={c.code} label={c.label} redeemed={false} copied={copied} onCopy={copy} />
            ))}
          </div>
        </div>
      )}

      {redeemed.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Already redeemed — go ask for a testimonial</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {redeemed.map((c) => (
              <CodeRow key={c.code} code={c.code} label={c.label} redeemed redeemedEmail={c.redeemed_email} copied={copied} onCopy={copy} />
            ))}
          </div>
        </div>
      )}

      {active.length === 0 && redeemed.length === 0 && (
        <p className="mt-5 rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-4 text-center text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 inline mr-1.5 text-accent" />
          No beta codes yet. Mint 5 above and DM them to teachers you meet.
        </p>
      )}
    </div>
  );
};

export default AdminBetaCodesCard;
